import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import type { DnaSlice } from "@/lib/bookDna";

interface BookDnaProps {
  identity: string;
  slices: DnaSlice[];
  compact?: boolean;
}

const BookDna = ({ identity, slices, compact }: BookDnaProps) => {
  if (slices.length === 0) {
    return (
      <div className="rounded-2xl border border-border bg-card p-5 mb-8">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-primary" />
          <h3 className="font-display text-lg font-bold">Your Book DNA</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Save a few books or react to what you read — your reader identity will start to take shape.
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-border bg-gradient-to-br from-card to-card/60 p-5 mb-8 shadow-sm"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h3 className="font-display text-lg font-bold">Your Book DNA</h3>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{identity}</p>
        </div>
      </div>

      <div className="h-3 w-full rounded-full overflow-hidden flex bg-muted">
        {slices.map((s) => (
          <motion.div
            key={s.label}
            initial={{ width: 0 }}
            animate={{ width: `${s.percent}%` }}
            transition={{ duration: 0.7 }}
            className={`${s.color} h-full`}
            style={{ width: `${s.percent}%` }}
            title={`${s.label} — ${s.percent}%`}
          />
        ))}
      </div>

      <div className={`grid ${compact ? "grid-cols-2" : "grid-cols-2 md:grid-cols-4"} gap-2 mt-3`}>
        {slices.map((s) => (
          <div key={s.label} className="flex items-center gap-2 text-xs">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${s.color}`} />
            <span className="text-muted-foreground truncate">{s.label}</span>
            <span className="ml-auto font-semibold">{s.percent}%</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default BookDna;
